/**
 * Equipment booking — pure domain logic (no Preact, no IO).
 *
 * The availability engine ports the Excel register's formula
 * (Registro Attrezzatura+inventario.xlsx):
 *
 *   DISPONIBILITÀ = MAX(0, TOTALE − still-out − lost)
 *
 * extended with a time dimension:
 *
 *   free(item, from, to) = max(0, total_qty                 // the fixed roster
 *                                 − lost_qty                // gone for good
 *                                 − out_of_service_qty      // DAMAGED: set aside until repaired
 *                                 − collected-not-returned units
 *                                 − reserved units overlapping [from, to])
 *
 * The SAME arithmetic runs server-side inside the
 * equipment_create_booking() RPC (migration 21) with the item rows
 * locked — that call is authoritative and race-safe; this module is the
 * instant client-side view of it. The fixture tests in
 * equipment.test.ts pin both to the spreadsheet's own scenarios.
 */
import { ADMIN_NAME, type AppState } from "../types";

/* ---------- Identity ---------- */

/** The signed-in PERSON's roster name. For admins the app's `currentUser`
 *  is the virtual ADMIN_NAME (they act on everyone's calendar), so a
 *  booking must not be attributed to "Administrator": resolve their own
 *  row through profileId → profilesByRoster (loaded for admins at boot).
 *  Falls back to currentUser (non-admins, demo) and finally "". */
export function selfRosterName(
  s: Pick<AppState, "currentUser" | "profileId" | "profilesByRoster">,
): string {
  if (s.currentUser && s.currentUser !== ADMIN_NAME) return s.currentUser;
  const own = s.profileId
    ? Object.values(s.profilesByRoster).find((p) => p.id === s.profileId)
    : undefined;
  return own?.roster_name ?? s.currentUser ?? "";
}

/* ---------- Row shapes (mirror the equipment_* tables) ---------- */

export interface EquipmentCategory {
  id: number;
  name: string;
  sort_order: number;
}

export interface EquipmentItem {
  id: number;
  category_id: number;
  name: string;
  description: string;
  /** The roster: every unit the unit owns, fixed unless kit is bought or
   *  dismissed. */
  total_qty: number;
  /** Gone for good. */
  lost_qty: number;
  /** DAMAGED units, set aside and not bookable until an admin marks them
   *  repaired (UI label "Damaged"; DB column name is historical,
   *  migration 31). */
  out_of_service_qty: number;
  /** Unused since migration 31 (folded into out_of_service_qty). */
  damaged_qty?: number;
  unit_notes: string;
  is_active: boolean;
}

export type BookingStatus =
  | "PENDING"
  | "APPROVED"
  | "COLLECTED"
  /** Kit is back and the admin's return report awaits the holder's
   *  confirmation (migration 29). Closed by the holder or by an admin. */
  | "RETURN_PENDING"
  | "RETURNED"
  | "REJECTED"
  | "CANCELLED";

export interface EquipmentBooking {
  id: number;
  reference: string;
  /** auth.uid() of whoever created it — the stable "is this mine?" key. */
  requester_id: string | null;
  requester_name: string;
  on_behalf_of: string;
  /** Colleague who will collect the kit on the holder's behalf (migration
   *  32). The booking stays the holder's; optional column. */
  pickup_delegate?: string;
  pickup_date: string; // YYYY-MM-DD
  pickup_time: string; // HH:MM
  return_date: string;
  return_time: string;
  purpose: string;
  requester_notes: string;
  status: BookingStatus;
  decided_by: string;
  decided_at: string | null;
  decision_reason: string;
  collected_at: string | null;
  collected_by: string;
  pickup_supervisor: string;
  returned_at: string | null;
  returned_by: string;
  return_supervisor: string;
  admin_notes: string;
  created_at: string;
}

export interface EquipmentBookingLine {
  id: number;
  booking_id: number;
  item_id: number;
  qty_requested: number;
  qty_approved: number | null; // null until an admin decides
  qty_returned: number;
  qty_damaged: number;
  damage_note: string;
  qty_lost: number;
  line_notes: string;
}

/* ---------- Email notifications (outbox rows, migration 23) ---------- */

export type NotificationKind =
  | "created"
  | "approved"
  | "rejected"
  | "cancelled"
  | "collected"
  | "returned"
  | "return_changed"
  | "transfer_proposed"
  | "transfer_completed"
  | "transfer_declined"
  | "return_report_issued"
  | "return_disputed"
  | "pickup_delegate";

export interface EquipmentNotification {
  id: number;
  booking_id: number;
  kind: NotificationKind;
  created_at: string;
  claimed_at: string | null;
  sent_at: string | null;
  attempts: number;
  last_error: string | null;
  recipients: string[];
  resend_id: string | null;
  /** Small per-row context written by the trigger (return_changed:
   *  old_date/old_time/new_date/new_time). */
  payload: Record<string, string | null> | null;
}

/** Who each notification goes to — mirrors the Edge Function's
 *  recipientsFor(). "admins" = admins the super admin ticked 🔔. */
export const NOTIFICATION_AUDIENCE: Record<NotificationKind, string> = {
  created: "Ticked admins · requester (confirmation)",
  cancelled: "Ticked admins + requester",
  return_changed: "Requester + ticked admins",
  approved: "Requester",
  rejected: "Requester",
  collected: "Requester",
  returned: "Requester",
  transfer_proposed: "New holder · ticked admins",
  transfer_completed: "Previous holder + new holder + ticked admins",
  transfer_declined: "Previous holder + ticked admins",
  return_report_issued: "Holder",
  return_disputed: "Ticked admins",
  pickup_delegate: "Delegate + holder + ticked admins",
};

/* ---------- "What happens next" ---------- */

export type NextTone = "info" | "ok" | "warn" | "bad" | "muted";

/** One plain sentence per booking telling a first-time user where the
 *  booking stands and what happens next. Neutral voice so the same line
 *  reads right for the holder and for an admin. */
export function nextStep(
  b: EquipmentBooking,
  todayIso: string,
  deps: { when: (date: string, time: string) => string; person: (name: string) => string },
): { text: string; tone: NextTone } {
  const pickup = deps.when(b.pickup_date, b.pickup_time);
  const back = deps.when(b.return_date, b.return_time);
  const delegate = b.pickup_delegate ? ` (${deps.person(b.pickup_delegate)} collects)` : "";
  switch (b.status) {
    case "PENDING":
      return { text: `Waiting for General Affairs to approve. Pickup planned ${pickup}.`, tone: "info" };
    case "APPROVED":
      return b.pickup_date < todayIso
        ? { text: `Approved, pickup was planned ${pickup} and has not been registered yet.`, tone: "warn" }
        : { text: `Approved. Collect it from General Affairs ${pickup}${delegate}.`, tone: "ok" };
    case "COLLECTED":
      return b.return_date < todayIso
        ? { text: `Overdue: it was due back ${back}. Return it, or change the date.`, tone: "bad" }
        : { text: `Out. Return it to General Affairs by ${back}.`, tone: "info" };
    case "RETURN_PENDING":
      return { text: "Back at General Affairs. The return report is waiting for the holder's confirmation.", tone: "warn" };
    case "RETURNED":
      return { text: `Closed${b.returned_at ? ` ${deps.when(b.returned_at.slice(0, 10), "")}` : ""}.`, tone: "muted" };
    case "REJECTED":
      return { text: b.decision_reason ? `Not approved: ${b.decision_reason}` : "Not approved.", tone: "muted" };
    case "CANCELLED":
      return { text: "Cancelled.", tone: "muted" };
  }
}

export type NotificationState = "sent" | "skipped" | "retrying" | "failed" | "pending";

/** Derived delivery state of an outbox row. The sender retries up to
 *  5 times. A closed row (sent_at set) counts as SENT when at least one
 *  recipient received it — last_error may then carry a note about a
 *  part that didn't go out — and SKIPPED when nobody did. */
export function notificationState(n: EquipmentNotification): NotificationState {
  if (n.sent_at) return n.recipients.length > 0 ? "sent" : "skipped";
  if (n.attempts >= 5) return "failed";
  if (n.attempts > 0) return "retrying";
  return "pending";
}

/* ---------- Status presentation ---------- */

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  PENDING: "Pending",
  APPROVED: "Approved",
  COLLECTED: "Collected",
  RETURN_PENDING: "Return to confirm",
  RETURNED: "Returned",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

/** Label that never crashes on a status this client doesn't know yet
 *  (an older cached bundle meeting a row written by a newer one). */
export function statusLabel(status: string): string {
  return (BOOKING_STATUS_LABEL as Record<string, string | undefined>)[status]
    ?? status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");
}

/** Legal state-machine transitions — the single map both the UI and the
 *  tests consult. Anything not listed is an error. */
export const BOOKING_TRANSITIONS: Record<BookingStatus, BookingStatus[]> = {
  PENDING: ["APPROVED", "REJECTED", "CANCELLED"],
  APPROVED: ["COLLECTED", "CANCELLED"],
  COLLECTED: ["RETURN_PENDING"],
  RETURN_PENDING: ["RETURNED"],
  RETURNED: [],
  REJECTED: [],
  CANCELLED: [],
};

/** Statuses in which the holder still "has" the booking: they can edit
 *  purpose / notes / return date and propose a hand-over. */
export function isOpenBooking(status: BookingStatus): boolean {
  return status === "PENDING" || status === "APPROVED" || status === "COLLECTED";
}

export function canTransition(from: BookingStatus, to: BookingStatus): boolean {
  return BOOKING_TRANSITIONS[from].includes(to);
}

/** The expected return date can be moved (by the booker or an admin)
 *  until the return is registered. Mirrors equipment_update_return_date. */
export function canEditReturnDate(status: BookingStatus): boolean {
  return status === "PENDING" || status === "APPROVED" || status === "COLLECTED";
}

/** A file attached to a booking — the signed handover sheet, typically.
 *  Bytes live in the private `equipment-docs` storage bucket. */
export interface EquipmentDocument {
  id: number;
  booking_id: number;
  kind: "signed_sheet" | "pickup" | "return" | "other" | "return_report";
  path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  created_at: string;
  /** Set when an admin bulk-downloaded the file and removed it from
   *  storage (migration 29): the row stays as a tombstone. */
  archived_at?: string | null;
  archived_by?: string;
}

/* ---------- Ownership transfer (migration 28) ---------- */

export interface EquipmentTransfer {
  id: number;
  booking_id: number;
  from_name: string;
  from_profile_id: string | null;
  to_name: string;
  to_profile_id: string;
  proposed_by: string;
  proposed_at: string;
  accepted_at: string | null;
  approved_at: string | null;
  approved_by: string;
  status: "PENDING" | "COMPLETED" | "DECLINED" | "CANCELLED";
  closed_at: string | null;
  closed_by: string;
  close_reason: string;
}

/** Who still has to say yes on an open transfer (both sides are
 *  notified at once and may answer in any order). Null once closed. */
export function transferWaitingOn(t: EquipmentTransfer): "both" | "new holder" | "admin" | null {
  if (t.status !== "PENDING") return null;
  if (!t.accepted_at && !t.approved_at) return "both";
  if (!t.accepted_at) return "new holder";
  if (!t.approved_at) return "admin";
  return null;
}

/* ---------- Return report (migration 29) ---------- */

export interface ReturnReportLine {
  line_id: number;
  item_id: number;
  item_name: string;
  due: number;
  returned: number;
  lost: number;
  damaged: number;
  out_of_service: number;
  damage_note: string;
}

export interface EquipmentReturnReport {
  id: number;
  booking_id: number;
  version: number;
  status: "ISSUED" | "DISPUTED" | "CONFIRMED";
  lines: ReturnReportLine[];
  returned_by: string;
  received_by: string;
  admin_notes: string;
  declared_by: string;
  declared_at: string;
  confirmed_by: string;
  confirmed_at: string | null;
  closed_without_confirmation: boolean;
  close_note: string;
  disputed_at: string | null;
  dispute_note: string;
}

/** Outcome from the report's own lines (same rule as returnOutcome). */
export function reportOutcome(lines: ReturnReportLine[]): string {
  const lost = lines.reduce((a, l) => a + l.lost, 0);
  const damaged = lines.reduce((a, l) => a + l.damaged, 0);
  if (lost > 0) return "Incomplete";
  if (damaged > 0) return "Complete (with damage)";
  return "Complete";
}

/** ASCII file-name-safe version of a person's name: "Lóránt Kovács" →
 *  "Lorant-Kovacs". */
export function fileSlug(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";
}

/** Archive file name for a return report — reference, physical return
 *  date, holder — so a folder of them sorts and reads naturally. */
export function returnReportFileName(b: Pick<EquipmentBooking, "reference">, r: Pick<EquipmentReturnReport, "declared_at" | "returned_by">): string {
  return `${b.reference}_${r.declared_at.slice(0, 10)}_${fileSlug(r.returned_by)}_return-report.pdf`;
}

/** Admins may hard-delete any booking from Manage → Requests (and from
 *  the Edit modal). Before the kit moves there is nothing to warn about;
 *  once it has, the row is part of the register's history, so the
 *  confirm dialog spells out what disappears. Item stock and cumulative
 *  lost / out-of-service counts live on the items and are never touched. */
export function deleteWarning(status: BookingStatus): string | null {
  switch (status) {
    case "COLLECTED":
      return "This kit is currently out. Deleting the record removes the pickup (who collected it, when) and the items will show as available again.";
    case "RETURN_PENDING":
      return "The kit is back but the return report is still awaiting the holder's confirmation. Deleting removes the report and the pickup / return records; lost or out-of-service units declared in it are NOT applied to the items.";
    case "RETURNED":
      return "This is a closed loan in the register's history. The return record (who returned it, condition, lost or damaged units) and its return report disappear from the register and its export. Item stock and lost counts are unaffected.";
    default:
      return null;
  }
}

/** Derived return outcome — never typed by hand. Lost units make the
 *  return "Incomplete"; damage alone stays complete but visible (the
 *  spreadsheet under-reported this — damage-only showed as Completo). */
export function returnOutcome(lines: EquipmentBookingLine[]): string {
  const lost = lines.reduce((a, l) => a + l.qty_lost, 0);
  const damaged = lines.reduce((a, l) => a + l.qty_damaged, 0);
  if (lost > 0) return "Incomplete";
  if (damaged > 0) return "Complete (with damage)";
  return "Complete";
}

/* ---------- Pickup/return time slots ---------- */

/** 09:30 → 16:30 inclusive, 15-minute steps. */
export const SLOT_START_MIN = 9 * 60 + 30;
export const SLOT_END_MIN = 16 * 60 + 30;
export const SLOT_STEP_MIN = 15;

export function timeSlots(): string[] {
  const out: string[] = [];
  for (let m = SLOT_START_MIN; m <= SLOT_END_MIN; m += SLOT_STEP_MIN) {
    out.push(
      `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`,
    );
  }
  return out;
}

/* ---------- Availability engine ---------- */

/** Units a line still holds out. COLLECTED: approved (or requested,
 *  pre-decision) minus what came back one way or another. RETURN_PENDING:
 *  the kit is back but units declared LOST stay out until the report is
 *  confirmed and they move to the item's lost_qty — the same rule as the
 *  SQL equipment_units_out() (migration 29). */
function outstandingQty(l: EquipmentBookingLine, status: BookingStatus): number {
  const due = l.qty_approved ?? l.qty_requested;
  return due - l.qty_returned - (status === "COLLECTED" ? l.qty_lost : 0);
}

/** Date-window overlap on inclusive [pickup, return] ranges. */
export function windowsOverlap(
  aFrom: string,
  aTo: string,
  bFrom: string,
  bTo: string,
): boolean {
  return aFrom <= bTo && aTo >= bFrom;
}

export interface ItemAvailability {
  item: EquipmentItem;
  /** Units free for the requested window (the stepper's maximum). */
  free: number;
  /** Units reserved by PENDING/APPROVED bookings overlapping the window. */
  reserved: number;
  /** Units still out: COLLECTED bookings, plus units declared lost on
   *  RETURN_PENDING ones (any window). */
  out: number;
  /** Units set aside as damaged (item.out_of_service_qty) — not bookable
   *  until repaired from Inventory. */
  damaged: number;
  /** Damage notes from the return history, for the hover tooltip. */
  damageNotes: string[];
  /** Presentation status for the availability table. */
  status: "disponibile" | "prenotato" | "in_uso" | "non_disponibile" | "fuori_servizio";
}

/** Presentation status from the computed numbers — shared by the local
 *  engine and the RPC-aggregate path so the two can never disagree. */
function deriveStatus(
  item: EquipmentItem,
  free: number,
  reserved: number,
  out: number,
): ItemAvailability["status"] {
  const capacity = item.total_qty - item.lost_qty - item.out_of_service_qty;
  if (capacity <= 0) return "fuori_servizio";
  if (free === 0) return "non_disponibile";
  if (out > 0) return "in_uso";
  if (reserved > 0) return "prenotato";
  return "disponibile";
}

/** Row shape of the equipment_availability() RPC (migration 22) — the
 *  privacy-safe path for non-admins: per-item aggregates, no booking
 *  rows. */
export interface AvailabilityAggregate {
  item_id: number;
  free: number;
  reserved: number;
  out_now: number;
}

/** Merge RPC aggregates with the catalogue into the same shape the
 *  local engine produces. Items missing from the aggregate rows (e.g.
 *  freshly added) fall back to their unreserved capacity. */
export function availabilityFromAggregates(
  items: EquipmentItem[],
  rows: AvailabilityAggregate[],
): ItemAvailability[] {
  const byId = new Map<number, AvailabilityAggregate>();
  rows.forEach((r) => byId.set(r.item_id, r));
  return items
    .filter((i) => i.is_active)
    .map((item) => {
      const capacity = Math.max(0, item.total_qty - item.lost_qty - item.out_of_service_qty);
      const r = byId.get(item.id);
      const free = r ? r.free : capacity;
      const reserved = r ? r.reserved : 0;
      const out = r ? r.out_now : 0;
      return { item, free, reserved, out, damaged: item.out_of_service_qty, damageNotes: [], status: deriveStatus(item, free, reserved, out) };
    });
}

/** Compute free quantity for every item over one pass of the bookings —
 *  the client-side mirror of the RPC's per-item query (no N+1). */
export function computeAvailability(
  items: EquipmentItem[],
  bookings: EquipmentBooking[],
  lines: EquipmentBookingLine[],
  from: string,
  to: string,
): ItemAvailability[] {
  const byBooking = new Map<number, EquipmentBooking>();
  bookings.forEach((b) => byBooking.set(b.id, b));

  const reserved = new Map<number, number>();
  const out = new Map<number, number>();
  const notes = new Map<number, string[]>();
  lines.forEach((l) => {
    const b = byBooking.get(l.booking_id);
    if (!b) return;
    if (b.status === "RETURNED" && l.qty_damaged > 0 && l.damage_note) {
      notes.set(l.item_id, [...(notes.get(l.item_id) ?? []), `${b.reference}: ${l.damage_note}`]);
    }
    if (b.status === "COLLECTED" || b.status === "RETURN_PENDING") {
      const n = outstandingQty(l, b.status);
      if (n > 0) out.set(l.item_id, (out.get(l.item_id) ?? 0) + n);
    } else if (
      (b.status === "PENDING" || b.status === "APPROVED") &&
      windowsOverlap(b.pickup_date, b.return_date, from, to)
    ) {
      const n = l.qty_approved ?? l.qty_requested;
      if (n > 0) reserved.set(l.item_id, (reserved.get(l.item_id) ?? 0) + n);
    }
  });

  return items
    .filter((i) => i.is_active)
    .map((item) => {
      const r = reserved.get(item.id) ?? 0;
      const o = out.get(item.id) ?? 0;
      const capacity = item.total_qty - item.lost_qty - item.out_of_service_qty;
      const free = Math.max(0, capacity - o - r);
      return {
        item, free, reserved: r, out: o,
        damaged: item.out_of_service_qty, damageNotes: notes.get(item.id) ?? [],
        status: deriveStatus(item, free, r, o),
      };
    });
}

export const AVAILABILITY_STATUS_META: Record<
  ItemAvailability["status"],
  { label: string; icon: string; cls: string }
> = {
  disponibile: { label: "Available", icon: "✓", cls: "eqa-free" },
  prenotato: { label: "Partially reserved", icon: "◔", cls: "eqa-reserved" },
  in_uso: { label: "Partially in use", icon: "◔", cls: "eqa-reserved" },
  non_disponibile: { label: "Not available", icon: "✕", cls: "eqa-none" },
  fuori_servizio: { label: "All units damaged or lost", icon: "⛔", cls: "eqa-oos" },
};

/* ---------- Booking-window validation (client convenience; the RPC
   re-validates dates/quantities server-side) ---------- */

export interface WindowCheckDeps {
  isWeekend: (y: number, m: number, d: number) => boolean;
  getHolidayName: (isoDate: string) => string | null;
  closures: Record<string, string>;
}

/** Returns a human error for an unusable pickup/return date, or null. */
export function dateProblem(iso: string, todayIso: string, deps: WindowCheckDeps): string | null {
  if (!iso) return "Pick a date";
  if (iso < todayIso) return "Date is in the past";
  const [y, m, d] = iso.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return "Invalid date";
  if (deps.isWeekend(y, m - 1, d)) return "Weekends are not available";
  const hol = deps.getHolidayName(iso);
  if (hol) return `Public holiday (${hol})`;
  if (deps.closures[iso] !== undefined) {
    const label = deps.closures[iso];
    return `Company closure${label ? ` (${label})` : ""}`;
  }
  return null;
}

/** Overdue = physically out (or approved-and-unfetched) past the
 *  expected return date. Display-side indicator only. */
export function isOverdue(b: EquipmentBooking, todayIso: string): boolean {
  return (
    (b.status === "COLLECTED" && b.return_date < todayIso) ||
    (b.status === "APPROVED" && b.pickup_date < todayIso)
  );
}
